/* Doctor nudge template editor (Compliance Insights Wave 5).
 *
 * Each row = one of the 3 auto-nudge reasons (LOW_COMPLIANCE / OPEN_ISSUE /
 * NO_RECENT_LOG). Doctor edits the WhatsApp body, inserts variable chips,
 * previews the rendered output, and saves. "Reset to default" reverts to
 * the built-in copy without losing the dirty draft (so it acts as a
 * "preview default" + commit-on-save flow).
 *
 * Backend: GET / PUT / DELETE /api/doctor/nudge-templates[/{reason}].
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
    Warning, ClockUser, ChatCircleDots, FloppyDisk, ArrowCounterClockwise,
    Sparkle, Eye, Spinner, CheckCircle,
} from "@phosphor-icons/react";


const REASON_META = {
    LOW_COMPLIANCE: {
        label: "Low compliance",
        hint: "Sent when a patient's 7-day wear-time average drops below 16h.",
        Icon: ClockUser,
        tone: "amber",
        vars: ["patient_first_name", "patient_name", "avg_hours_7d", "clinic_name", "doctor_name"],
    },
    OPEN_ISSUE: {
        label: "Open issue",
        hint: "Sent when a patient has an unresolved medium / high-severity issue.",
        Icon: Warning,
        tone: "rose",
        vars: ["patient_first_name", "patient_name", "issue_label", "clinic_name", "doctor_name"],
    },
    NO_RECENT_LOG: {
        label: "No recent log",
        hint: "Sent when a patient hasn't logged wear-time in 4+ days.",
        Icon: ChatCircleDots,
        tone: "violet",
        vars: ["patient_first_name", "patient_name", "days_since_last_log", "clinic_name", "doctor_name"],
    },
};

// Sample context the preview uses. Doctors see a realistic rendered output
// without having to send an actual nudge.
const SAMPLE_CTX = {
    patient_first_name: "Priya",
    patient_name: "Priya Sharma",
    avg_hours_7d: "14.5",
    days_since_last_log: "5",
    issue_label: "broken attachment",
    clinic_name: "Smile Studio",
    doctor_name: "Dr. Riya",
};


function renderPreview(body, ctx) {
    if (!body) return "";
    return body.replace(/\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g, (_, key) => ctx[key] ?? "");
}


function toneClasses(tone, on) {
    const map = {
        amber: on ? "bg-amber-100 text-amber-800 border-amber-200" : "text-amber-700",
        rose: on ? "bg-rose-100 text-rose-700 border-rose-200" : "text-rose-700",
        violet: on ? "bg-violet-100 text-violet-800 border-violet-200" : "text-violet-700",
    };
    return map[tone] || "";
}


export default function NudgeTemplatesEditor() {
    const [rows, setRows] = useState(null);
    const [loadErr, setLoadErr] = useState(null);

    useEffect(() => {
        api.get("/doctor/nudge-templates")
            .then((r) => setRows(r.data || []))
            .catch((e) => setLoadErr(e.response?.data?.detail || "Couldn't load templates"));
    }, []);

    const update = (reason, patch) => {
        setRows((curr) => (curr || []).map((r) => (r.reason === reason ? { ...r, ...patch } : r)));
    };

    if (loadErr) {
        return (
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
                {loadErr}
            </div>
        );
    }
    if (rows === null) {
        return (
            <div className="text-center text-sm text-muted-foreground py-8">
                <Spinner size={16} className="inline-block animate-spin mr-2" /> Loading templates…
            </div>
        );
    }

    return (
        <div className="space-y-5" data-testid="nudge-templates-editor">
            <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 text-sm text-foreground/80 flex gap-3">
                <Sparkle size={20} weight="duotone" className="text-primary shrink-0 mt-0.5" />
                <div>
                    <div className="font-semibold text-foreground mb-1">Make the voice yours.</div>
                    These are the WhatsApp messages auto-prefilled when you tap the green
                    "WhatsApp" button on the compliance dashboard. Edit them to match how
                    your clinic actually speaks. Variables in <code className="bg-foreground/10 px-1 py-0.5 rounded text-[11px]">{"{curly braces}"}</code> are
                    replaced with each patient's data when you nudge them.
                </div>
            </div>

            {rows.map((row) => (
                <TemplateCard
                    key={row.reason}
                    row={row}
                    onChange={(patch) => update(row.reason, patch)}
                />
            ))}
        </div>
    );
}


function TemplateCard({ row, onChange }) {
    const meta = REASON_META[row.reason] || {};
    const Icon = meta.Icon || ChatCircleDots;
    const [draft, setDraft] = useState(row.body);
    const [showPreview, setShowPreview] = useState(false);
    const [saving, setSaving] = useState(false);
    const [resetting, setResetting] = useState(false);

    // Keep local draft in sync if the parent (page-level reload) replaces row.
    useEffect(() => { setDraft(row.body); }, [row.body]);

    const isDirty = draft !== row.body;
    const previewText = useMemo(() => renderPreview(draft, SAMPLE_CTX), [draft]);
    const charCount = draft.length;

    const insertVar = (varName) => {
        const placeholder = `{${varName}}`;
        const ta = document.getElementById(`tpl-textarea-${row.reason}`);
        if (!ta) {
            setDraft((d) => d + placeholder);
            return;
        }
        const start = ta.selectionStart ?? draft.length;
        const end = ta.selectionEnd ?? draft.length;
        const next = draft.slice(0, start) + placeholder + draft.slice(end);
        setDraft(next);
        // Restore caret right after the inserted placeholder.
        requestAnimationFrame(() => {
            ta.focus();
            const pos = start + placeholder.length;
            ta.setSelectionRange(pos, pos);
        });
    };

    const save = async () => {
        if (charCount < 5) {
            toast.error("Message is too short (min 5 characters)");
            return;
        }
        setSaving(true);
        try {
            const r = await api.put(`/doctor/nudge-templates/${row.reason}`, { body: draft });
            onChange({ body: r.data.body, is_custom: true, updated_at: r.data.updated_at });
            toast.success("Template saved");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't save");
        } finally {
            setSaving(false);
        }
    };

    const reset = async () => {
        if (!row.is_custom) {
            // No backend state — just revert the dirty draft to default.
            setDraft(row.default_body);
            return;
        }
        setResetting(true);
        try {
            await api.delete(`/doctor/nudge-templates/${row.reason}`);
            onChange({ body: row.default_body, is_custom: false, updated_at: null });
            setDraft(row.default_body);
            toast.success("Reverted to default");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't reset");
        } finally {
            setResetting(false);
        }
    };

    return (
        <div
            className="rounded-2xl border border-border bg-card overflow-hidden"
            data-testid={`tpl-card-${row.reason}`}
        >
            {/* Header */}
            <div className={`px-5 py-3 border-b border-border flex items-center gap-3 ${toneClasses(meta.tone, true)}`}>
                <div className="w-8 h-8 rounded-full bg-white/70 grid place-items-center shrink-0">
                    <Icon size={15} weight="bold" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-base leading-tight">{meta.label}</div>
                    <div className="text-[11px] opacity-90 leading-snug">{meta.hint}</div>
                </div>
                {row.is_custom ? (
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/80 text-foreground">
                        Custom
                    </span>
                ) : (
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/40 text-foreground/70">
                        Default
                    </span>
                )}
            </div>

            <div className="p-5 space-y-4">
                {/* Variable chips */}
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                        Variables — click to insert at cursor
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        {(meta.vars || []).map((v) => (
                            <button
                                key={v}
                                type="button"
                                onClick={() => insertVar(v)}
                                className="inline-flex items-center gap-1 px-2.5 h-7 rounded-full bg-foreground/5 hover:bg-primary/10 text-xs font-mono text-foreground/80 transition border border-transparent hover:border-primary/30"
                                data-testid={`tpl-var-${row.reason}-${v}`}
                            >
                                {`{${v}}`}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Editor */}
                <div>
                    <Textarea
                        id={`tpl-textarea-${row.reason}`}
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        rows={4}
                        className="font-sans text-sm leading-relaxed"
                        data-testid={`tpl-textarea-${row.reason}`}
                        maxLength={2000}
                    />
                    <div className="flex items-center justify-between mt-1.5">
                        <button
                            type="button"
                            onClick={() => setShowPreview((v) => !v)}
                            className="text-[11px] font-semibold text-primary hover:underline inline-flex items-center gap-1"
                            data-testid={`tpl-toggle-preview-${row.reason}`}
                        >
                            <Eye size={11} weight="bold" /> {showPreview ? "Hide preview" : "Show preview"}
                        </button>
                        <div className="text-[10px] font-mono tabular-nums text-muted-foreground">{charCount} / 2000</div>
                    </div>
                </div>

                {/* Preview */}
                {showPreview && (
                    <div
                        className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 text-sm leading-relaxed text-foreground/90"
                        data-testid={`tpl-preview-${row.reason}`}
                    >
                        <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-800 mb-1 flex items-center gap-1.5">
                            <CheckCircle size={11} weight="fill" /> Sample patient: Priya Sharma
                        </div>
                        <div className="whitespace-pre-wrap break-words">{previewText}</div>
                    </div>
                )}

                {/* Actions */}
                <div className="flex items-center justify-end gap-2 pt-1 border-t border-border/60">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={reset}
                        disabled={resetting || (!row.is_custom && !isDirty)}
                        data-testid={`tpl-reset-${row.reason}`}
                    >
                        <ArrowCounterClockwise size={13} className="mr-1" weight="bold" />
                        {row.is_custom ? "Reset to default" : "Discard"}
                    </Button>
                    <Button
                        onClick={save}
                        disabled={saving || !isDirty}
                        size="sm"
                        className="rounded-full"
                        data-testid={`tpl-save-${row.reason}`}
                    >
                        <FloppyDisk size={13} className="mr-1" weight="bold" />
                        {saving ? "Saving…" : "Save"}
                    </Button>
                </div>
            </div>
        </div>
    );
}
